import { makeAutoObservable, runInAction } from 'mobx';
import type RootStore from './RootStore';

interface PerformanceMetric {
    name: string;
    duration: number;
    timestamp: number;
    metadata?: Record<string, any>;
}

export class PerformanceStore {
    rootStore: RootStore;
    metrics: Map<string, number[]> = new Map();
    detailedMetrics: PerformanceMetric[] = [];
    maxMetricsHistory = 1000;
    performanceThresholds = {
        fast: 400,    // < 100ms
        moderate: 1000, // < 500ms
        slow: 4000,   // < 1000ms
    };

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false,
        });
        this.rootStore = rootStore;

        // Setup Performance Observer for more detailed metrics
        this.setupPerformanceObserver();
    }

    private setupPerformanceObserver() {
        if ('PerformanceObserver' in window) {
            // Observe navigation timing
            const navObserver = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    if (entry.entryType === 'navigation') {
                        this.recordNavigationTiming(entry as PerformanceNavigationTiming);
                    }
                }
            });

            navObserver.observe({ entryTypes: ['navigation'] });

            // Observe resource timing
            const resourceObserver = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    if (entry.entryType === 'resource') {
                        this.recordResourceTiming(entry as PerformanceResourceTiming);
                    }
                }
            });

            resourceObserver.observe({ entryTypes: ['resource'] });
        }
    }

    private recordNavigationTiming(entry: PerformanceNavigationTiming) {
        const metrics = {
            domContentLoaded: entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
            domComplete: entry.domComplete - entry.domInteractive,
            loadComplete: entry.loadEventEnd - entry.loadEventStart,
            timeToInteractive: entry.domInteractive - entry.fetchStart,
        };

        Object.entries(metrics).forEach(([name, duration]) => {
            this.recordMetric(name, duration);
        });
    }

    private recordResourceTiming(entry: PerformanceResourceTiming) {
        if (entry.initiatorType === 'xmlhttprequest' || entry.initiatorType === 'fetch') {
            this.recordMetric(`api_${entry.name}`, entry.duration, {
                transferSize: entry.transferSize,
                decodedBodySize: entry.decodedBodySize,
            });
        }
    }

    async measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
        const start = performance.now();

        try {
            const result = await fn();
            const duration = performance.now() - start;
            this.recordMetric(name, duration);

            return result;
        } catch (error) {
            const duration = performance.now() - start;
            this.recordMetric(name, duration, { error: true });
            throw error;
        }
    }

    measure<T>(name: string, fn: () => T): T {
        const start = performance.now();

        try {
            const result = fn();
            const duration = performance.now() - start;
            this.recordMetric(name, duration);

            return result;
        } catch (error) {
            const duration = performance.now() - start;
            this.recordMetric(name, duration, { error: true });
            throw error;
        }
    }

    private recordMetric(name: string, duration: number, metadata?: Record<string, any>) {
        runInAction(() => {
            // Update simple metrics
            const metrics = this.metrics.get(name) || [];
            metrics.push(duration);
            this.metrics.set(name, metrics.slice(-100)); // Keep last 100

            // Add to detailed metrics
            this.detailedMetrics.push({
                name,
                duration,
                timestamp: Date.now(),
                metadata,
            });

            // Trim detailed metrics
            if (this.detailedMetrics.length > this.maxMetricsHistory) {
                this.detailedMetrics = this.detailedMetrics.slice(-this.maxMetricsHistory);
            }

            // Check for performance issues
            this.checkPerformanceThreshold(name, duration);
        });
    }

    private checkPerformanceThreshold(name: string, duration: number) {
        if (duration > this.performanceThresholds.slow) {
            console.warn(`Performance Warning: ${name} took ${duration.toFixed(2)}ms (SLOW)`);

            // Only add notifications in development mode
            if (process.env.NODE_ENV === 'development' && this.rootStore.notificationStore && duration > 4000) {
                this.rootStore.notificationStore.addNotification({
                    message: `Slow operation: ${name} (${(duration / 1000).toFixed(1)}s)`,
                    options: {
                        variant: 'warning',
                        autoHideduration: 5000,
                    },
                });
            }
        }
    }

    getAverageTime(name: string): number {
        const metrics = this.metrics.get(name) || [];
        if (metrics.length === 0) return 0;
        return metrics.reduce((a, b) => a + b, 0) / metrics.length;
    }

    getPercentile(name: string, percentile: number): number {
        const metrics = this.metrics.get(name) || [];
        if (metrics.length === 0) return 0;

        const sorted = [...metrics].sort((a, b) => a - b);
        const index = Math.ceil((percentile / 100) * sorted.length) - 1;
        return sorted[index] || 0;
    }

    getPerformanceGrade(name: string): 'fast' | 'moderate' | 'slow' | 'very-slow' {
        const avg = this.getAverageTime(name);

        if (avg < this.performanceThresholds.fast) return 'fast';
        if (avg < this.performanceThresholds.moderate) return 'moderate';
        if (avg < this.performanceThresholds.slow) return 'slow';
        return 'very-slow';
    }

    getMetricsSummary(name: string) {
        const metrics = this.metrics.get(name) || [];
        if (metrics.length === 0) {
            return {
                count: 0,
                average: 0,
                min: 0,
                max: 0,
                p50: 0,
                p95: 0,
                p99: 0,
                grade: 'unknown' as const,
            };
        }

        return {
            count: metrics.length,
            average: this.getAverageTime(name),
            min: Math.min(...metrics),
            max: Math.max(...metrics),
            p50: this.getPercentile(name, 50),
            p95: this.getPercentile(name, 95),
            p99: this.getPercentile(name, 99),
            grade: this.getPerformanceGrade(name),
        };
    }

    getAllMetricsSummary() {
        const summaries: Record<string, ReturnType<typeof this.getMetricsSummary>> = {};

        this.metrics.forEach((_, name) => {
            summaries[name] = this.getMetricsSummary(name);
        });

        return summaries;
    }

    clearMetrics(name?: string) {
        if (name) {
            this.metrics.delete(name);
            this.detailedMetrics = this.detailedMetrics.filter(m => m.name !== name);
        } else {
            this.metrics.clear();
            this.detailedMetrics = [];
        }
    }

    // Browser Performance APIs
    getMemoryUsage() {
        if ('memory' in performance) {
            const memory = (performance as any).memory;
            return {
                usedJSHeapSize: memory.usedJSHeapSize,
                totalJSHeapSize: memory.totalJSHeapSize,
                jsHeapSizeLimit: memory.jsHeapSizeLimit,
                percentUsed: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
            };
        }
        return null;
    }

    markAndMeasure(markName: string, measureName?: string, startMark?: string) {
        performance.mark(markName);

        if (measureName && startMark) {
            try {
                performance.measure(measureName, startMark, markName);
                const measure = performance.getEntriesByName(measureName, 'measure')[0];
                if (measure) {
                    this.recordMetric(measureName, measure.duration);
                }
            } catch (error) {
                console.error('Failed to measure:', error);
            }
        }
    }
}